<template>
  <div>
    <!-- Fees still owed for the past school years. Kept outside the loading
         branch so it does not flicker while fees are refetched. -->
    <PastYearsArrearsAlert :student-ids="selectedStudents" />

    <div v-if="loading"><a-skeleton active /></div>
    <div v-else>
      <!-- Header Section -->
      <InvoiceHeader
        :selectedStudents="selectedStudents"
        :parent="parents[0] || {}"
        :restrict-to-parent="!!(student && student._id)"
        :loading-valider="loadingValider"
        :disabled-valider="disabledChangeEspaceCheque"
        :disable-invalid-avance="invalidAvance"
        :selected-payment-method="selectedPaymentMethod"
        @select-payment-method="selectPaymentMethod"
        @select-payment-type-fr="selectPaymentTypeFR"
        @valid-payment="validPayment"
        @filter-option="filterOption"
        @select-student="selectStudent"
        @caisse-change="onCaisseChange"
      />

      <!-- Reduction Section -->
      <ReductionSection
        :reduction-type="reductionType"
        :tnd-on-trans-reduction="TndOnTransReduction"
        :on-trans-reduction="onTransReduction"
        :selected-type-f-r="selectedTypeFR"
        :total="total"
        :with-avance="payWithAvance"
        :avance-amount="avanceAmount"
        :fiscal-timbre="FISCAL"
        :timbre-applicable="invoiceFiscal.timbreApplicable"
        @update-reduction="updateReduction"
        @check-all="checkEmALL"
      />

      <!-- Advance Section 
      <AdvanceSection
        v-if="dataAvance.length > 0 && withAvance"
        :data-avance="dataAvance"
        :total="total"
        :montant-avance="montantAvance"
        @update-montant="montantAvance = $event"
      />-->

      <!-- Payment Form -->
      <a-form class="card-body row overflow" :form="form">
        <!-- Cheque Information Section -->
        <ChequeSection
          v-if="selectedPaymentMethod.includes('CHEQUE')"
          :cheques-data="chequesData"
          :cheque-vide="chequeVide"
          @update-cheque="updateCheque"
          @add-cheque="addCheque"
          @remove-cheque="removeCheque"
          @calculate-montant="debouncedcalMontantCashDistro"
        />

        <!-- Traite Information Section -->
        <TraiteSection
          class="mt-2"
          v-if="selectedPaymentMethod.includes('TRAITE')"
          :traites-data="traitesData"
          :traite-vide="traiteVide"
          @update-traite="updateTraite"
          @add-traite="addTraite"
          @remove-traite="removeTraite"
          @calculate-montant="debouncedcalMontantCashDistro"
        />

        <!-- Virement Information Section -->
        <VirementSection
          class="mt-2"
          v-if="selectedPaymentMethod.includes('VIREMENT')"
          :virements-data="virementsData"
          :virement-vide="virementVide"
          @update-virement="updateVirement"
          @add-virement="addVirement"
          @remove-virement="removeVirement"
          @calculate-montant="debouncedcalMontantCashDistro"
        />

        <!-- TPE Information Section -->
        <TpeSection
          v-if="selectedPaymentMethod.includes('TPE')"
          :tpe-data="tpeData"
          @update-tpe-data="updateTpeData"
        />

        <!-- Cheque and Cash Distribution -->
        <ChequeCashDistribution
          v-show="selectedPaymentMethod.length >= 2"
          :total="total"
          :disabled="disabledChangeEspaceCheque"
          :with-avance="payWithAvance"
          :avance-amount="avanceAmount"
          :form="form"
        />

        <!-- Payment Cards Section -->
        <PaymentCardsSection
          :all-data-payment="allDataPayement"
          :checked="checked"
          :on-trans-reduction="onTransReduction"
          :selected-type-f-r="selectedTypeFR"
          :fiscal-timbre="FISCAL"
          @update-fee-selection="updateFeeSelection"
          @toggle-all-fees="toggleAllFees"
          @update-avance-distribution="handleAvanceDistribution"
          @update-invalid-avance="handleInvalidAvance"
        />
      </a-form>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import "jspdf-autotable";
import apiClient from "@/services/axios";
import { PageHeader } from "ant-design-vue";
import { mapState } from "vuex";
import debounce from "lodash.debounce";

// Import child components
import InvoiceHeader from "./components/InvoiceHeader.vue";
import ReductionSection from "./components/ReductionSection.vue";
import ChequeSection from "./components/ChequeSection.vue";
import ChequeCashDistribution from "./components/ChequeCashDistribution.vue";
import PaymentCardsSection from "./components/PaymentCardsSection.vue";
import TraiteSection from "./components/traiteSection.vue";
import VirementSection from "./components/VirementSection.vue";
import TpeSection from "./components/tpeSection.vue";
import PastYearsArrearsAlert from "@/components/status/PastYearsArrearsAlert.vue";
import { printInvoice } from "@/utilities/printInvoice";
import {
  resolveDocumentTemplate,
  resolveBuildingInvoiceFiscal,
} from "@/utilities/documentTemplateConfig";

const CHEQUE_VIDE = {
  num: null,
  dateEchance: null,
  bank: undefined,
  titulaire: null,
  montant: 0,
  type: "CHEQUE",
};
const TRAITE_VIDE = {
  num: null,
  dateEchance: null,
  bank: undefined,
  rib: null,
  montant: 0,
  type: "TRAITE",
};
const VIREMENT_VIDE = {
  num: null,
  caisseBank: undefined,
  titulaire: null,
  montant: 0,
  file: null,
  type: "VIREMENT",
};
export default {
  name: "InvoiceBuilder",

  components: {
    "a-page-header": PageHeader,
    InvoiceHeader,
    ReductionSection,
    ChequeSection,
    ChequeCashDistribution,
    PaymentCardsSection,
    TraiteSection,
    VirementSection,
    TpeSection,
    PastYearsArrearsAlert,
  },

  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return (this.user.building || []).find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    /** Effective facture fiscal rates for this school (env fallback). */
    invoiceFiscal() {
      return resolveBuildingInvoiceFiscal(this.activeBuilding);
    },
    /** Timbre amount to add on Facture totals (0 when not applicable). */
    FISCAL() {
      return Number(this.invoiceFiscal.timbreAmount) || 0;
    },
  },

  props: {
    classroomName: {
      required: false,
      type: String,
      default: "",
    },
    idParent: {
      required: false,
      type: String,
      default: "",
    },
    student: {
      required: false,
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      // API Data
      dataAvance: [],
      allDataPayement: [],
      parents: [],
      selectedStudents: [],
      // Caisse receiving the payment, chosen in the header when the user owns several.
      selectedCaisseId: null,
      // Form and UI State
      loading: false,
      loadingValider: false,
      form: this.$form.createForm(this),
      selectedPaymentMethod: ["ESPESE"],
      selectedTypeFR: "R",
      // Reduction State
      onTransReduction: 0,
      TndOnTransReduction: 0,
      reductionType: "amount",
      // withAvance: false,
      // montantAvance: null,

      // Payment State
      checked: {},
      disabledChangeEspaceCheque: false,
      especeMethodeMissing: false,
      selectAllValue: true,

      // Cheque State
      chequesData: [],
      chequeVide: CHEQUE_VIDE,
      // Traite State
      traitesData: [],
      traiteVide: TRAITE_VIDE,

      // Virement State
      virementsData: [],
      virementVide: VIREMENT_VIDE,

      // Computed Values
      total: {
        bReduction: 0,
        aReduction: 0,
        reduction: 0,
      },

      // Debounced Functions
      debouncedShowTotal: null,
      debouncedcalMontantCashDistro: null,

      tpeData: {
        caisseBank: undefined,
        transactionId: "",
        montant: 0,
      },

      // Avance Distribution State
      avanceDistribution: {},
      invalidAvance: false,
      payWithAvance: false,
      avanceAmount: 0,
    };
  },

  async created() {
    // Handle case where no student is provided
    if (this.student && this.student._id) {
      this.selectedStudents.push(this.student._id);
    } else {
      this.selectedStudents.push(...this.studentsFromRoute());
    }
    // If no student is provided, we'll let the user select from the dropdown

    await this.refreshBuildingConfig();
    this.initializeComponent();
    this.setupDebouncedFunctions();
    await this.fetchPayments();
  },

  beforeDestroy() {
    this.onTransReduction = 0;
  },

  methods: {
    /**
     * Reload Building.config from Root so facture fiscal (timbre/TVA/retenue)
     * matches Accounting settings even if vuex session is stale.
     */
    async refreshBuildingConfig() {
      const building = this.activeBuilding;
      if (!building?._id) return;
      try {
        const res = await apiClient.post("/buildings/filter", {
          query: { _id: building._id },
        });
        const fresh = Array.isArray(res.data) ? res.data[0] : null;
        if (!fresh?.config) return;
        const buildings = (this.user.building || []).map((b) =>
          String(b._id) === String(building._id)
            ? { ...b, config: fresh.config }
            : b
        );
        this.$store.commit("user/SET_BUILDING", buildings);
      } catch (e) {
        console.log(e);
      }
    },

    // Students preselected by a deep link, e.g. the previous-year arrears
    // shortcut which reopens this screen on another school year.
    studentsFromRoute() {
      const students = this.$route && this.$route.query.students;
      if (!students) return [];
      return String(students)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    },

    // Initialization Methods
    initializeComponent() {
      // Reset payment method specific data arrays
      this.chequesData = [];
      this.traitesData = [];
      this.virementsData = [];

      this.chequesData.push({ ...this.chequeVide });
      this.traitesData.push({ ...this.traiteVide });
      this.virementsData.push({ ...this.virementVide });
      this.tpeData = { caisseBank: undefined, transactionId: "", montant: 0 };
      this.onTransReduction = 0;

      const building = this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
      if (building) {
        this.reductionType = building.reductionType || "pourcentage";
      }
    },

    setupDebouncedFunctions() {
      this.debouncedShowTotal = debounce(() => {
        this.showTotal(null, true);
      }, 200);

      this.debouncedcalMontantCashDistro = debounce(() => {
        this.calMontantCashDistro();
      }, 200);
    },

    // Data Fetching Methods
    async fetchPayments() {
      this.loading = true;

      try {
        await Promise.all([
          this.fetchParents(),
          this.fetchPaymentData(),
          // this.getAvances(),
        ]);
      } catch (error) {
        console.error("Error fetching payments:", error);
        this.$message.error(this.$t("recette.erreurChargementDonnees"));
      } finally {
        this.loading = false;
      }
    },

    async fetchParents() {
      // Handle case where no parent ID is provided
      if (!this.idParent) {
        // Don't fetch parents, but don't show warning since this is expected
        this.parents = [];
        return;
      }

      const response = await apiClient.post("/parents/filter", {
        query: { _id: this.idParent },
        aggregation: [
          {
            $lookup: {
              from: "students",
              localField: "students",
              foreignField: "_id",
              as: "students",
            },
          },
          {
            $project: {
              father: 1,
              mother: 1,
              students: {
                _id: 1,
                firstName: 1,
                lastName: 1,
                status: 1,
              },
            },
          },
        ],
      });
      this.parents = response.data;
    },

    async fetchPaymentData() {
      // Handle case where no students are selected
      if (!this.selectedStudents || this.selectedStudents.length === 0) {
        // Don't fetch payment data, but don't show warning since this is expected initially
        this.allDataPayement = [];
        return;
      }

      this.loading = true;
      this.resetPaymentState();
      const response = await apiClient.post("/payment/pvalidator", {
        studentIds: this.selectedStudents,
      });
      this.allDataPayement = response.data;
      this.initializePaymentData();
      this.loading = false;
    },
    initializePaymentData() {
      for (const payment of this.allDataPayement) {
        payment.montantEspece = 0;
        payment.montantCheque = 0;
        payment.montantTotal = 0;
        this.$set(this.checked, payment._id, {});

        if (payment.fees) {
          for (const fee of payment.fees) {
            if (fee.fullyPaid === false || fee.fullyPaid === null) {
              this.$set(this.checked[payment._id], fee.feeName, false);
            }
          }
        }
      }
    },

    resetPaymentState() {
      this.selectedPaymentMethod = ["ESPESE"];
      this.selectedTypeFR = "R";
      // this.montantAvance = null;
      this.allDataPayement = [];
    },

    // Event Handlers
    selectPaymentMethod(selectedPaymentMethod, e) {
      this.selectedPaymentMethod = selectedPaymentMethod;
      // Reset form fields for payment methods not included in selectedPaymentMethod
      const paymentFields = {
        CHEQUE: { field: "montantCheque", data: "chequesData" },
        TRAITE: { field: "montantTraite", data: "traitesData" },
        VIREMENT: { field: "montantVirement", data: "virementsData" },
        TPE: { field: "montantTPE", data: "tpeData" },
      };
      Object.entries(paymentFields).forEach(([method, { field, data }]) => {
        if (!this.selectedPaymentMethod.includes(method)) {
          const resetValue = {};
          resetValue[field] = 0;
          this.form.setFieldsValue(resetValue);
          // Reset the corresponding data arrays/objects
          if (method === "CHEQUE") {
            this.chequesData = [{ ...this.chequeVide }];
          } else if (method === "TRAITE") {
            this.traitesData = [{ ...this.traiteVide }];
          } else if (method === "VIREMENT") {
            this.virementsData = [{ ...this.virementVide }];
          } else if (method === "TPE") {
            this.tpeData = { caisseBank: undefined, transactionId: "", montant: 0 };
          }
        }
      });
      this.calMontantCashDistro();
    },

    selectPaymentTypeFR(selectedTypeFR, e) {
      this.selectedTypeFR = selectedTypeFR.target.value;
    },

    onCaisseChange(caisseId) {
      this.selectedCaisseId = caisseId;
    },

    async selectStudent(selectedStudents, e) {
      // Keep the fee selections made so far; the reset below replaces `checked`.
      const previousChecked = this.checked;
      this.selectedStudents = selectedStudents;

      // Reset all variables except the specified ones
      this.resetAllVariables();

      // Reset form fields
      this.resetFormFields();

      // Re-execute initialization methods
      this.initializeComponent();
      this.setupDebouncedFunctions();
      await this.fetchPaymentData();
      this.restoreFeeSelections(previousChecked);
    },

    // Re-apply earlier selections, keyed by payment (one per student), only
    // for fees that are still selectable in the refreshed data.
    restoreFeeSelections(previousChecked) {
      let restored = false;
      for (const paymentId in this.checked) {
        for (const feeName in this.checked[paymentId]) {
          if (previousChecked[paymentId]?.[feeName]) {
            this.$set(this.checked[paymentId], feeName, true);
            restored = true;
          }
        }
      }
      if (restored) this.$nextTick(() => this.showTotal());
    },

    // New method to reset all variables except the specified ones
    resetAllVariables() {
      // API Data
      this.dataAvance = [];
      this.allDataPayement = [];

      // Form and UI State
      this.loading = false;
      this.loadingValider = false;

      // Reduction State
      this.onTransReduction = 0;
      this.TndOnTransReduction = 0;

      // Payment State
      this.checked = {};
      this.disabledChangeEspaceCheque = false;
      this.especeMethodeMissing = false;
      this.selectAllValue = true;

      // Cheque State
      this.chequesData = [];

      // Traite State
      this.traitesData = [];

      // Virement State
      this.virementsData = [];

      // Computed Values
      this.total = {
        bReduction: 0,
        aReduction: 0,
        reduction: 0,
      };

      // TPE Data
      this.tpeData = {
        caisseBank: undefined,
        transactionId: "",
        montant: 0,
      };

      // Avance Distribution State
      this.avanceDistribution = {};
      this.invalidAvance = false;
      this.payWithAvance = false;
      this.avanceAmount = 0;
    },

    // New method to reset form fields
    resetFormFields() {
      // Reset form to initial values
      this.form.resetFields();
    },

    updateReduction(type, value) {
      if (type === "percentage") {
        this.onTransReduction = value;
      } else {
        this.TndOnTransReduction = value;
      }
      this.debouncedShowTotal();
    },

    updateCheque(index, field, value) {
      this.chequesData[index][field] = value;
    },

    addCheque() {
      this.chequesData.push({ ...this.chequeVide });
    },

    removeCheque(index) {
      this.chequesData.splice(index, 1);
    },

    updateTraite(index, field, value) {
      this.traitesData[index][field] = value;
    },

    addTraite() {
      this.traitesData.push({ ...this.traiteVide });
    },

    removeTraite(index) {
      this.traitesData.splice(index, 1);
    },

    updateVirement(index, field, value) {
      this.virementsData[index][field] = value;
    },

    addVirement() {
      this.virementsData.push({ ...this.virementVide });
    },

    removeVirement(index) {
      this.virementsData.splice(index, 1);
    },

    updateFeeSelection(paymentId, feeName, checked) {
      this.$set(this.checked[paymentId], feeName, checked);
      this.showTotal();
    },

    handleAvanceDistribution(avanceDistribution, payWithAvance, avanceAmount) {
      // Store the avance distribution data for use in payment processing
      this.avanceDistribution = avanceDistribution;
      this.payWithAvance = payWithAvance;
      this.avanceAmount = avanceAmount;
      this.debouncedcalMontantCashDistro();
    },
    handleInvalidAvance(val) {
      this.invalidAvance = val;
    },

    // Calculation Methods
    showTotal(value, reductionChanged = false) {
      this.calculateTotalAmounts();
      this.updatePaymentAmounts();

      if (this.selectedPaymentMethod.length > 0) {
        this.calMontantCashDistro();
      }
    },

    calculateTotalAmounts() {
      this.total.bReduction = 0;
      this.total.aReduction = 0;
      this.total.reduction = 0;

      for (const paymentId in this.checked) {
        const record = this.allDataPayement.find((p) => p._id == paymentId);
        record.montantTotal = 0;

        for (const feeName in this.checked[paymentId]) {
          if (this.checked[paymentId][feeName]) {
            const fee = record.fees.find((p) => p.feeName == feeName);
            record.montantTotal += Number(fee.feeAfterReduction);
          }
        }

        this.total.bReduction += record.montantTotal;
      }

      this.applyReduction();
    },

    applyReduction() {
      if (this.reductionType === "amount") {
        this.total.aReduction = this.total.bReduction;
        this.total.aReduction -= Math.min(
          this.TndOnTransReduction,
          this.total.aReduction
        );
        this.total.reduction = this.total.bReduction - this.total.aReduction;

        if (this.TndOnTransReduction >= this.total.bReduction) {
          this.onTransReduction = 100;
        } else if (this.total.bReduction > 0) {
          this.onTransReduction =
            100 - (100 * this.total.aReduction) / this.total.bReduction;
        } else {
          this.onTransReduction = 0;
        }
        // .toFixed(3) returns a string, so convert back to number if needed:
        this.total.aReduction = Number(this.total.aReduction.toFixed(3));
      } else {
        for (const paymentId in this.checked) {
          const record = this.allDataPayement.find((p) => p._id == paymentId);
          for (const feeName in this.checked[paymentId]) {
            if (this.checked[paymentId][feeName]) {
              const fee = record.fees.find((p) => p.feeName == feeName);
              this.total.aReduction +=
                Number(fee.feeAfterReduction) *
                ((100 - this.onTransReduction) / 100);
            }
          }
        }
        this.total.reduction = this.total.bReduction - this.total.aReduction;
        this.total.aReduction = Number(this.total.aReduction.toFixed(3));
      }
    },

    updatePaymentAmounts() {
      this.allDataPayement.forEach((payment) => {
        payment.montantTotal =
          payment.montantTotal -
          (payment.montantTotal * this.onTransReduction) / 100;
      });
    },

    calMontantCashDistro() {
      const totalCheq = this.chequesData.reduce(
        (sum, c) => sum + (c.montant || 0),
        0
      );
      const totalTraite = this.traitesData.reduce(
        (sum, t) => sum + (t.montant || 0),
        0
      );
      const totalVirement = this.virementsData.reduce(
        (sum, v) => sum + (v.montant || 0),
        0
      );
      const totalTpe = this.tpeData.montant;
      let totalHorsEspace = totalCheq + totalTraite + totalVirement + totalTpe;
      totalHorsEspace =
        totalHorsEspace > 0 ? Number(totalHorsEspace.toFixed(3)) : 0;
      let totalEsp =
        this.total.aReduction > 0
          ? Number(this.total.aReduction) +
            (this.selectedTypeFR == "F" ? Number(this.FISCAL) : 0) -
            Number(totalHorsEspace)
          : 0;
      if (this.payWithAvance) totalEsp = this.avanceAmount - totalHorsEspace;
      this.form.setFieldsValue({
        montantCheque: totalCheq,
        montantTraite: totalTraite,
        montantVirement: totalVirement,
        montantEspece: totalEsp,
        montantTPE: totalTpe,
      });
      if (!this.selectedPaymentMethod.includes("ESPESE") && totalEsp > 0) {
        this.especeMethodeMissing = true;
      } else {
        this.especeMethodeMissing = false;
      }
      this.disabledChangeEspaceCheque = totalEsp < 0;
    },

    // Action Methods
    checkEmALL() {
      for (const id in this.checked) {
        for (const key in this.checked[id]) {
          this.$set(this.checked[id], key, this.selectAllValue);
        }
      }

      this.selectAllValue = !this.selectAllValue;
      this.showTotal(null, false);
      this.calMontantCashDistro();
    },

    toggleAllFees(paymentId) {
      const payment = this.allDataPayement.find((p) => p._id === paymentId);
      if (!payment || !payment.fees) return;

      const allSelected = this.areAllFeesSelected(paymentId);

      for (const key in this.checked[paymentId]) {
        this.$set(this.checked[paymentId], key, !allSelected);
      }

      this.showTotal(null, false);
    },

    // Validation Methods
    notZeroAmount() {
      for (const id in this.checked) {
        for (const key in this.checked[id]) {
          if (this.checked[id][key]) return true;
        }
      }
      return false;
    },

    areAllFeesSelected(paymentId) {
      const payment = this.allDataPayement.find((p) => p._id === paymentId);
      if (!payment || !payment.fees) return false;

      const unpaidFees = payment.fees.filter(
        (fee) => fee.fullyPaid === false || fee.fullyPaid === null
      );
      if (unpaidFees.length === 0) return false;

      return unpaidFees.every((fee) => this.checked[paymentId][fee.feeName]);
    },

    // Payment Processing
    async validPayment(type, addressedTo) {
      this.loadingValider = true;

      if (
        !this.selectedPaymentMethod ||
        this.selectedPaymentMethod.length == 0
      ) {
        this.$message.warning(this.$t("recette.selectionnerModePaiement"));
        this.loadingValider = false;
        return;
      }
      if (this.especeMethodeMissing) {
        this.$message.warning(this.$t("recette.montantsNonEgaux"));
        this.loadingValider = false;
        return;
      }

      try {
        const validationResult = await this.validatePaymentForm();
        if (!validationResult.isValid) {
          this.$message.error(validationResult.message);
          this.loadingValider = false;
          return;
        }

        const paymentData = this.buildPaymentData(type);
        paymentData.typeFR = type;
        paymentData.parent = this.idParent;
        paymentData.parentName = this.parents[0]
          ? this.parents[0].father.firstName +
            " " +
            this.parents[0].father.lastName +
            " " +
            this.$t("recette.parentAnd") +
            " " +
            this.parents[0].mother.firstName +
            " " +
            this.parents[0].mother.lastName
          : "";
        paymentData.addressedTo = addressedTo;
        paymentData.paymentMethod = this.selectedPaymentMethod;
        // Sent only when the user owns several caisses; the server picks the single one otherwise.
        if (this.selectedCaisseId) {
          paymentData.caisseId = this.selectedCaisseId;
        }
        paymentData.montantHtBrut = this.total.bReduction;
        paymentData.withAvance = this.payWithAvance
          ? this.total.aReduction +
              (this.selectedTypeFR == "F" ? this.FISCAL : 0) ==
            this.avanceAmount
            ? false
            : true
          : false;
        const response = await apiClient.put(
          "/compV3/parent-invoice",
          paymentData
        );

        //await this.handleAdvanceDeduction(response.data.serialMulti);

        this.$message.success(this.$t("recette.paiementAjoute"));
        const invoiceRecord =
          (response.data && response.data.invoice) || response.data;
        await this.maybeAutoPrint(invoiceRecord, type);
        this.$emit("reload");
      } catch (error) {
        let errorMsg = this.$t("recette.paiementNonEffectuer");
        if (
          error.response &&
          error.response.data &&
          error.response.data.errorCode
        ) {
          errorMsg = this.$t(`errorCode.${error.response.data.errorCode}`);
        }
        console.log(error);
        this.$message.error(errorMsg);
      } finally {
        this.loadingValider = false;
      }
    },

    async validatePaymentForm() {
      return new Promise((resolve) => {
        this.form.validateFieldsAndScroll((err, values) => {
          if (err) {
            console.error("validation error", err);
            resolve({ isValid: false, message: this.$t("recette.paiementNonEffectuer") });
            return;
          }

          if (!this.notZeroAmount()) {
            resolve({
              isValid: false,
              message: this.$t("recette.nonValid"),
            });
            return;
          }

          // Check if selectedTypeFR is 'F' and any selected fees have remaining values
          if (this.selectedTypeFR === "F") {
            const hasFeesWithRemaining = this.checkFeesWithRemaining();
            if (hasFeesWithRemaining) {
              resolve({
                isValid: false,
                message: this.$t("recette.typeFactureFeesRestants"),
              });
              return;
            }
          }

          if (this.selectedPaymentMethod.length > 1) {
            const totalAmount =
              values.montantCheque +
              values.montantEspece +
              values.montantTraite +
              values.montantVirement +
              values.montantTPE;
            let total =
              Number(this.total.aReduction) +
              (this.selectedTypeFR == "F" ? Number(this.FISCAL) : 0);
            if (this.payWithAvance) total = this.avanceAmount;

            if (total != Number(totalAmount.toFixed(3))) {
              resolve({
                isValid: false,
                message: this.$t("recette.sommeMontantsEgal", {
                  amount: total.toFixed(3),
                }),
              });
              return;
            }
          } else if (!this.selectedPaymentMethod.includes("ESPESE")) {
            const totalAmount =
              values.montantCheque +
              values.montantTraite +
              values.montantVirement +
              values.montantTPE;
            let total =
              Number(this.total.aReduction) +
              (this.selectedTypeFR == "F" ? Number(this.FISCAL) : 0);
            if (this.payWithAvance) total = this.avanceAmount;

            if (totalAmount != Number(total.toFixed(3))) {
              resolve({
                isValid: false,
                message: this.$t("recette.montantEgal", {
                  amount: total.toFixed(3),
                }),
              });
              return;
            }
          }

          resolve({ isValid: true });
        });
      });
    },
    //user can't choose type 'Facture' when there are fees with remaining values
    checkFeesWithRemaining() {
      for (const paymentId in this.checked) {
        const payment = this.allDataPayement.find((p) => p._id === paymentId);
        if (!payment || !payment.fees) continue;

        for (const feeName in this.checked[paymentId]) {
          if (this.checked[paymentId][feeName]) {
            const fee = payment.fees.find((f) => f.feeName === feeName);
            if (fee && fee.remaining && fee.remaining > 0) {
              return true;
            }
          }
        }
      }
      return false;
    },

    buildPaymentData(type) {
      const data = {
        transactions: [],
        montantTotal: 0,
      };
      let modeId = 1;
      if (this.selectedPaymentMethod.length > 1) {
        const values = this.form.getFieldsValue();
        data.montantCheque = values.montantCheque;
        data.montantEspece = values.montantEspece;
        data.montantTraite = values.montantTraite;
        data.montantVirement = values.montantVirement;
        data.montantTPE = this.tpeData.montant;

        let traitesCheques = [];
        if (this.selectedPaymentMethod.includes("CHEQUE")) {
          traitesCheques = this.chequesData;
        }
        if (this.selectedPaymentMethod.includes("TRAITE")) {
          traitesCheques = traitesCheques.concat(this.traitesData);
        }
        if (this.selectedPaymentMethod.includes("VIREMENT")) {
          traitesCheques = traitesCheques.concat(this.virementsData);
        }
        data.cheques = traitesCheques;
        if (this.selectedPaymentMethod.includes("TPE")) {
          data.tpe = this.tpeData;
        }
        modeId = 3;
      } else if (this.selectedPaymentMethod.includes("CHEQUE")) {
        data.montantCheque = this.payWithAvance
          ? this.avanceAmount
          : this.chequesData.reduce((sum, c) => sum + (c.montant || 0), 0);
        data.cheques = this.chequesData;
        modeId = 2;
      } else if (this.selectedPaymentMethod.includes("TRAITE")) {
        data.montantTraite = this.payWithAvance
          ? this.avanceAmount
          : this.traitesData.reduce((sum, t) => sum + (t.montant || 0), 0);
        data.cheques = this.traitesData;
        modeId = 4;
      } else if (this.selectedPaymentMethod.includes("VIREMENT")) {
        data.montantVirement = this.payWithAvance
          ? this.avanceAmount
          : this.virementsData.reduce((sum, v) => sum + (v.montant || 0), 0);
        data.cheques = this.virementsData;
        modeId = 6;
      } else if (this.selectedPaymentMethod.includes("TPE")) {
        data.tpe = this.tpeData;
        data.montantTPE = this.payWithAvance
          ? this.avanceAmount
          : this.tpeData.montant;
        modeId = 5;
      } else {
        data.montantEspece = this.payWithAvance
          ? this.avanceAmount
          : Number(this.total.aReduction) +
            (this.selectedTypeFR == "F" ? Number(this.FISCAL) : 0);
        modeId = 1;
      }

      data.montantTotal =
        Number(data.montantEspece || 0) +
        Number(data.montantCheque || 0) +
        Number(data.montantTraite || 0) +
        Number(data.montantVirement || 0) +
        Number(data.montantTPE || 0);
      data.transactions = this.buildTransactions(modeId, type);

      return data;
    },

    buildTransactions(modeId, type) {
      const transactions = [];
      //const avanceListReglement = [];

      for (const payment of this.allDataPayement) {
        const fees = {};
        let onInscription = false;
        let totalAvancePerPayment = 0;
        for (const fee of payment.fees) {
          if (this.checked[payment._id][fee.feeName]) {
            let remaining = 0;
            let feeAmount =
              (Number(fee.feeAfterReduction) * (100 - this.onTransReduction)) /
              100;
            let paid = feeAmount;
            let fullyPaid = true;

            if (
              this.avanceDistribution[payment._id] &&
              this.avanceDistribution[payment._id][fee.feeName]
            ) {
              paid = this.avanceDistribution[payment._id][fee.feeName];
              totalAvancePerPayment += paid;
              remaining =
                feeAmount - this.avanceDistribution[payment._id][fee.feeName];
              fullyPaid = remaining == 0;
            }
            fees[fee.feeName] = {
              fullyPaid: fullyPaid,
              paid: paid,
              remaining: remaining,
            };

            /*avanceListReglement.push(
              payment.typeAS == "A"
                ? "Activité/" + payment.name
                : "Scolarité/" + payment.name
            );*/
          }
        }

        if (this.checked[payment._id].inscription) onInscription = true;
        if (this.payWithAvance && totalAvancePerPayment > 0) {
          payment.montantTotal = totalAvancePerPayment;
        }
        if (Object.keys(fees).length > 0 || onInscription) {
          const transaction = {
            payment: payment._id,
            onTransReduction: parseFloat(this.onTransReduction).toFixed(3),
            typeAS: payment.typeAS,
            
            typeFR: type,
            fees,
            onInscription,
            date: Date.now(),
            mode: payment.mode,
            montantTotal: parseFloat(payment.montantTotal).toFixed(3),
            //v3
            student: payment.student._id,
            studentName: payment.student.fullName,
            daily: !!payment.daily,
            typeReglement: (() => {
              // Always persist the real class/activity name on the document,
              // even when the classroom is marked unavailable to parents
              // (enrollmentConfirmed === false).
              const isActivity = payment.typeAS == "A";
              const name = isActivity
                ? payment.name ||
                  (payment.activity && payment.activity.designation) ||
                  ""
                : (payment.classroom && payment.classroom.name) ||
                  payment.name ||
                  "";
              return (isActivity ? "Activité/" : "Scolarité/") + name;
            })(),
          };
          //this.addPaymentAmounts(transaction, modeId, payment);
          transactions.push(transaction);
        }
      }

      return transactions;
    },

    async maybeAutoPrint(invoiceRecord, typeFR) {
      try {
        // Ticket: auto-print applies to receipts only
        if (typeFR !== "R") return;
        if (!invoiceRecord) return;

        let building = (this.user.building || []).find(
          (el) => el.dbName == this.settings.activeBuilding
        );
        // Reload config from server so a freshly saved autoPrint flag is honored
        if (building?._id) {
          try {
            const res = await apiClient.post("/buildings/filter", {
              query: { _id: building._id },
            });
            const fresh = Array.isArray(res.data) ? res.data[0] : null;
            if (fresh?.config) {
              building = { ...building, config: fresh.config };
            }
          } catch (e) {
            console.log(e);
          }
        }

        const template = resolveDocumentTemplate(building, "receipt");
        if (!template.autoPrint) return;

        const record = {
          ...invoiceRecord,
          typeFR: invoiceRecord.typeFR || typeFR,
        };
        await printInvoice(record, this.settings, this.user);
      } catch (e) {
        console.log(e);
      }
    },

    // Utility Methods
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },

    // Event Emitters
    reload() {
      this.$emit("reload");
    },

    updateTpeData(field, value) {
      this.tpeData[field] = value;
      this.calMontantCashDistro();
    },
  },
};
</script>

<style scoped>
/* Keep existing styles but organize them better */
.reduction-section {
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.reduction-details {
  list-style: none;
  padding: 0;
  margin: 0;
}

.reduction-details li {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid #e9ecef;
}

.reduction-details li:last-child {
  border-bottom: none;
}

.reduction-details span {
  color: #6c757d;
}

.reduction-actions {
  text-align: right;
}

@media (max-width: 991px) {
  .reduction-actions {
    text-align: left;
    margin-top: 16px;
  }
}

td {
  padding: 0%;
}

.center {
  margin: auto;
  width: 50%;
}

.mycontent-left {
  border-right: 1px dashed #333;
}

/* Payment Cards Styles */
.payment-cards-section {
  margin-top: 20px;
}

.payment-cards-container {
  column-count: 2;
  column-gap: 20px;
}

.payment-card-wrapper {
  width: 100%;
  break-inside: avoid;
  margin-bottom: 20px;
}

.payment-card {
  width: 100%;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: auto;
  min-height: auto;
}

.payment-card-selected {
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
}

.payment-card .ant-card-head {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: none;
}

.payment-card .ant-card-head-title {
  color: white;
  font-weight: 600;
}

.payment-card .ant-card-extra {
  color: white;
}

.payment-card .ant-card-body {
  height: auto;
  min-height: auto;
}

.payment-info {
  background: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: none;
  color: white;
  border-radius: 6px;
  border-left: 4px solid #1890ff;
}

.payment-info small {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fees-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.fees-table .ant-table-thead > tr > th {
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.fees-table .ant-table-tbody > tr > td {
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 8px;
}

.fee-name {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.payment-distribution {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.fees-section {
  margin-top: 16px;
}

.fee-amount {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #1890ff;
}

.fee-final-amount {
  font-size: 12px;
  color: #52c41a;
  font-weight: 500;
  margin-top: 4px;
}

.card-actions {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  text-align: center;
}

.card-actions .ant-btn {
  border-radius: 20px;
  font-size: 12px;
  height: 32px;
  padding: 0 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .payment-cards-container {
    column-count: 1;
  }

  .payment-card {
    margin-bottom: 16px;
  }

  .fees-table {
    font-size: 12px;
  }
}

/* Animation for card selection */
.payment-card-selected {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }
  50% {
    box-shadow: 0 4px 20px rgba(24, 144, 255, 0.25);
  }
  100% {
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }
}

/* Tag styles */
.ant-tag {
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
}

/* Divider styles */
.ant-divider {
  margin: 16px 0;
  color: #666;
  font-weight: 500;
}

.ant-divider::before,
.ant-divider::after {
  border-top-color: #d9d9d9;
}

/* Clickable rows */
.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: #f5f5f5;
}

.clickable-row.ant-table-row-selected {
  background-color: #e6f7ff;
}
</style>
